/**
 * The forge's running cost in the page header, like the Arche Builder's
 * "N credits used": everything this forge has cost, and — while a turn runs —
 * what this turn has cost so far. The tooltip breaks the total down; on a
 * phone the page's menu writes the breakdown out (`forgeCostBreakdown`).
 */
import { Coins } from 'lucide-react';
import { formatCredits, formatDollars } from '@/lib/format-credits';
import type { ForgeCost } from '@/lib/api';

function hours(seconds: number): string {
  const h = seconds / 3600;
  return h >= 1 ? `${h.toFixed(1)} h` : `${Math.round(seconds / 60)} min`;
}

/** What the total is made of. A tooltip on a desktop; written out where there is no hover. */
export function forgeCostBreakdown(cost: ForgeCost): string {
  return (
    `Claude: ${formatCredits(cost.modelCredits)} credits · ` +
    `running time: ${formatCredits(cost.computeCredits)} credits (${hours(cost.activeSeconds)})`
  );
}

export function ForgeCostBadge({ cost }: { cost: ForgeCost | null }) {
  if (!cost) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
      title={forgeCostBreakdown(cost)}
      data-testid="forge-cost"
    >
      <Coins className="h-3.5 w-3.5" />
      <span>
        {formatCredits(cost.totalCredits)} credits ({formatDollars(cost.totalCredits)}) used
      </span>
      {cost.currentTurn && (
        <span className="rounded bg-muted px-1.5 py-0.5" data-testid="forge-cost-turn">
          this turn {formatCredits(cost.currentTurn.credits)}
          {cost.currentTurn.reservedCredits > 0 && '+'}
        </span>
      )}
    </div>
  );
}
